import { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import JSZip from "jszip";
import {
  MagnifyingGlass, SpinnerGap, ArrowsClockwise, Trash, Eye,
  CaretLeft, CaretRight, Copy, Check, DownloadSimple, Export,
} from "@phosphor-icons/react";

function downloadBlob(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function safeName(idea) {
  return (idea || "package").replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase().slice(0, 50);
}

function buildMarkdownContent(pkg) {
  return [
    `# ${pkg.idea}`,
    "",
    `> Generated by Quibn Content Studio`,
    `> Date: ${new Date(pkg.created_at).toLocaleDateString()}`,
    `> Audience: ${pkg.target_audience || "—"} | Objective: ${pkg.objective || "—"} | Tone: ${pkg.tone || "—"}`,
    `> Channels: ${(pkg.channels || []).join(", ") || "—"}`,
    "",
    "---",
    "",
    pkg.package_markdown || "",
  ].join("\n");
}

function buildJsonContent(pkg) {
  return JSON.stringify({
    package_id: pkg.package_id,
    idea: pkg.idea,
    target_audience: pkg.target_audience,
    objective: pkg.objective,
    tone: pkg.tone,
    channels: pkg.channels,
    desired_word_count: pkg.desired_word_count,
    source_notes: pkg.source_notes,
    ai_model: pkg.ai_model,
    package_markdown: pkg.package_markdown,
    created_at: pkg.created_at,
    updated_at: pkg.updated_at,
    regenerated_from: pkg.regenerated_from || null,
  }, null, 2);
}

function exportMarkdown(pkg) {
  downloadBlob(buildMarkdownContent(pkg), `${safeName(pkg.idea)}.md`, "text/markdown;charset=utf-8;");
  toast.success("Downloaded as .md");
}

function exportJson(pkg) {
  downloadBlob(buildJsonContent(pkg), `${safeName(pkg.idea)}.json`, "application/json;charset=utf-8;");
  toast.success("Downloaded as .json");
}

export function StudioHistoryTab({ api, onSelectPackage }) {
  const [packages, setPackages] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewPkg, setViewPkg] = useState(null);
  const [regenerating, setRegenerating] = useState("");
  const [copied, setCopied] = useState(false);
  const [bulkExporting, setBulkExporting] = useState(false);
  const limit = 10;

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/content-studio/packages?search=${encodeURIComponent(search)}&skip=${page * limit}&limit=${limit}`);
      setPackages(resp.data.packages || []);
      setTotal(resp.data.total || 0);
    } catch { toast.error("Failed to load packages"); }
    setLoading(false);
  }, [api, search, page]);

  useEffect(() => { fetchPackages(); }, [fetchPackages]);

  const handleDelete = async (pkgId) => {
    try {
      await api.delete(`/content-studio/packages/${pkgId}`);
      toast.success("Package deleted");
      fetchPackages();
      if (viewPkg?.package_id === pkgId) setViewPkg(null);
    } catch { toast.error("Failed to delete"); }
  };

  const handleRegenerate = async (pkgId) => {
    setRegenerating(pkgId);
    try {
      const resp = await api.post(`/content-studio/packages/${pkgId}/regenerate`);
      toast.success("Regenerated! New package created.");
      fetchPackages();
      setViewPkg(resp.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Regeneration failed"); }
    setRegenerating("");
  };

  const copyAll = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch {
      const ta = document.createElement("textarea"); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success("Copied!");
  };

  const handleBulkExport = async () => {
    setBulkExporting(true);
    try {
      // Fetch ALL packages (not just current page)
      const resp = await api.get(`/content-studio/packages?skip=0&limit=100`);
      const allPkgs = resp.data.packages || [];
      if (allPkgs.length === 0) { toast.error("No packages to export"); setBulkExporting(false); return; }

      const zip = new JSZip();
      const mdFolder = zip.folder("markdown");
      const jsonFolder = zip.folder("json");

      allPkgs.forEach((pkg, i) => {
        const name = `${String(i + 1).padStart(3, "0")}-${safeName(pkg.idea)}`;
        mdFolder.file(`${name}.md`, buildMarkdownContent(pkg));
        jsonFolder.file(`${name}.json`, buildJsonContent(pkg));
      });

      // Add a manifest
      const manifest = {
        exported_at: new Date().toISOString(),
        total_packages: allPkgs.length,
        packages: allPkgs.map(p => ({ package_id: p.package_id, idea: p.idea, created_at: p.created_at })),
      };
      zip.file("manifest.json", JSON.stringify(manifest, null, 2));

      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `quibn-packages-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${allPkgs.length} packages as .zip`);
    } catch (e) {
      console.error("Bulk export failed:", e);
      toast.error("Bulk export failed");
    }
    setBulkExporting(false);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4">
      {/* Search + Bulk Export */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <Input value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search packages by idea..." className="pl-10 bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="history-search" />
        </div>
        {total > 0 && (
          <Button onClick={handleBulkExport} disabled={bulkExporting} variant="outline"
            className="h-10 text-xs px-3 gap-1.5 shrink-0 border-[var(--border-strong)]" data-testid="bulk-export-btn">
            {bulkExporting ? <SpinnerGap size={14} className="animate-spin" /> : <Export size={14} />}
            {bulkExporting ? "Exporting..." : "Export All (.zip)"}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><SpinnerGap size={24} className="animate-spin text-[var(--text-muted)]" /></div>
      ) : packages.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-sm text-[var(--text-muted)]">{search ? "No packages match your search." : "No packages yet. Create one in the Create tab."}</p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {packages.map(pkg => (
              <div key={pkg.package_id} className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4 flex items-start justify-between gap-3 hover:border-[var(--text-muted)] transition-colors" data-testid={`pkg-${pkg.package_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[var(--text-primary)] truncate">{pkg.idea}</p>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    <span className="text-[10px] font-mono text-[var(--text-muted)]">{pkg.package_id}</span>
                    <span className="text-[10px] text-[var(--text-muted)]">{new Date(pkg.created_at).toLocaleDateString()}</span>
                    {pkg.channels?.length > 0 && (
                      <span className="text-[10px] text-[var(--text-muted)]">{pkg.channels.join(", ")}</span>
                    )}
                    {pkg.regenerated_from && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-400">regen</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button onClick={() => { setViewPkg(pkg); onSelectPackage?.(pkg); }} variant="ghost" size="sm" className="h-10 w-10 sm:h-8 sm:w-8 p-0" data-testid={`pkg-view-${pkg.package_id}`}><Eye size={14} /></Button>
                  <Button onClick={() => exportMarkdown(pkg)} variant="ghost" size="sm" className="h-10 w-10 sm:h-8 sm:w-8 p-0" title="Download .md" data-testid={`pkg-dl-md-${pkg.package_id}`}><DownloadSimple size={14} /></Button>
                  <Button onClick={() => handleRegenerate(pkg.package_id)} disabled={regenerating === pkg.package_id} variant="ghost" size="sm" className="h-10 w-10 sm:h-8 sm:w-8 p-0" data-testid={`pkg-regen-${pkg.package_id}`}>
                    {regenerating === pkg.package_id ? <SpinnerGap size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />}
                  </Button>
                  <Button onClick={() => handleDelete(pkg.package_id)} variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-300" data-testid={`pkg-del-${pkg.package_id}`}><Trash size={14} /></Button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3">
              <Button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} variant="outline" size="sm" className="h-8"><CaretLeft size={14} /></Button>
              <span className="text-xs text-[var(--text-muted)]">{page + 1} / {totalPages}</span>
              <Button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} variant="outline" size="sm" className="h-8"><CaretRight size={14} /></Button>
            </div>
          )}
        </>
      )}

      {/* Detail viewer */}
      <AnimatePresence>
        {viewPkg && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="bg-[var(--bg-card)] border border-[#FF4400]/20 rounded-xl p-5 space-y-3" data-testid="pkg-detail-view">
            <div className="flex items-center justify-between">
              <p className="text-xs font-mono text-[#FF4400]">{viewPkg.package_id}</p>
              <Button onClick={() => setViewPkg(null)} variant="ghost" size="sm" className="h-10 sm:h-7 text-xs">Close</Button>
            </div>
            <p className="text-sm font-medium text-[var(--text-primary)]">{viewPkg.idea}</p>
            <div className="max-h-[400px] overflow-y-auto bg-[var(--bg-base)] border border-[var(--border)] rounded-lg p-4">
              <div className="prose prose-sm max-w-none text-[var(--text-primary)] prose-headings:text-[var(--text-primary)] prose-p:text-[var(--text-secondary)]">
                <ReactMarkdown>{viewPkg.package_markdown}</ReactMarkdown>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => copyAll(viewPkg.package_markdown)} variant="outline" className="flex-1 h-9 rounded-lg gap-1.5 text-xs">
                {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied!" : "Copy Package"}
              </Button>
              <Button onClick={() => exportMarkdown(viewPkg)} variant="outline" className="h-9 rounded-lg gap-1.5 text-xs px-3" data-testid="pkg-detail-dl-md">
                <DownloadSimple size={12} /> .md
              </Button>
              <Button onClick={() => exportJson(viewPkg)} variant="outline" className="h-9 rounded-lg gap-1.5 text-xs px-3" data-testid="pkg-detail-dl-json">
                <DownloadSimple size={12} /> .json
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
